// React Error Code Decoder
// This helps decode minified React error messages in production

const REACT_ERROR_CODES: Record<string, string> = {
  '300': 'Rendered fewer hooks than expected. This may be caused by an accidental early return statement.',
  '310': 'Rendered more hooks than during the previous render.',
  '321': 'Invalid hook call. Hooks can only be called inside the body of a function component.',
  '301': 'Cannot read property of null',
  '302': 'Cannot call a class as a function',
  '303': 'Cannot access before initialization',
  '304': 'Assignment to constant variable',
  '305': 'Invalid hook call. Hooks can only be called inside the body of a function component.',
  '306': 'Invalid hook call. Hooks can only be called inside the body of a function component. Did you accidentally call React Hook outside of a React function?',
  '307': 'useEffect has a missing dependency',
  '308': 'useCallback has a missing dependency',
  '309': 'useMemo has a missing dependency',
  '311': 'Too many re-renders. React limits the number of renders to prevent an infinite loop.',
  '312': 'Cannot update a component while rendering a different component',
  '313': 'Cannot update during an existing state transition',
  '314': 'Cannot read properties of undefined (reading \'useState\')',
  '315': 'Cannot read properties of undefined (reading \'useEffect\')',
  '316': 'Cannot read properties of undefined (reading \'useContext\')',
  '317': 'Cannot read properties of undefined (reading \'useReducer\')',
  '318': 'Cannot read properties of undefined (reading \'useCallback\')',
  '319': 'Cannot read properties of undefined (reading \'useMemo\')',
  '320': 'Cannot read properties of undefined (reading \'useRef\')',
};

export function decodeReactError(errorMessage: string): string {
  // Check if it's a minified React error
  const reactErrorMatch = errorMessage.match(/Minified React error #(\d+)/);
  
  if (reactErrorMatch) {
    const errorCode = reactErrorMatch[1];
    const decodedMessage = REACT_ERROR_CODES[errorCode];
    
    if (decodedMessage) {
      return `React Error #${errorCode}: ${decodedMessage}`;
    }
    
    return `React Error #${errorCode}: Unknown error code. Visit https://react.dev/errors/${errorCode} for more information.`;
  }
  
  return errorMessage;
}

/**
 * Creates a user-friendly error message by combining original and decoded messages
 */
export function createFriendlyErrorMessage(error: Error): string {
  const decoded = decodeReactError(error.message);
  
  if (decoded !== error.message) {
    return decoded; // Return just the decoded message for cleaner display
  }
  
  return error.message;
}

/**
 * Determines if an error is a React hooks-related error
 */
export function isHooksError(errorMessage: string): boolean {
  const hooksErrorCodes = ['300', '305', '306', '310', '321'];
  const reactErrorMatch = errorMessage.match(/Minified React error #(\d+)/);
  
  if (reactErrorMatch) {
    return hooksErrorCodes.includes(reactErrorMatch[1]);
  }
  
  // Check for common hooks error patterns in non-minified messages
  const hooksPatterns = [
    /rendered fewer hooks/i,
    /rendered more hooks/i,
    /order of hooks/i,
    /hook.*called outside/i,
    /hook.*called inside.*callback/i
  ];
  
  return hooksPatterns.some(pattern => pattern.test(errorMessage));
}

export function enhanceErrorWithContext(error: Error, additionalContext: Record<string, any> = {}): Record<string, any> {
  const decodedMessage = decodeReactError(error.message);
  const friendlyMessage = createFriendlyErrorMessage(error);
  
  return {
    originalMessage: error.message,
    decodedMessage,
    friendlyMessage,
    errorName: error.name,
    stack: error.stack,
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : 'unknown',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    ...additionalContext,
    // React-specific debugging info
    isReactError: error.message.includes('Minified React error'),
    isHooksError: isHooksError(error.message),
    isRenderError: decodedMessage.toLowerCase().includes('render'),
  };
}

// Hook to automatically decode and log React errors
export function useReactErrorDecoder() {
  const logError = (error: Error, context?: Record<string, any>) => {
    const enhancedError = enhanceErrorWithContext(error, context);
    
    console.group('🔍 React Error Decoder');
    console.error('Original Error:', error);
    console.error('Decoded Message:', enhancedError.decodedMessage);
    console.error('Full Context:', enhancedError);
    console.groupEnd();
    
    return enhancedError;
  };
  
  return { logError };
}
